import { createContext, useContext, useState, type ReactNode } from 'react';

interface AssistantContextValue {
  isOpen: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  prompt: string;
  openWithPrompt: (prompt: string) => void;
  fileIds: string[];
  openWithFileIds: (fileIds: string[]) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [prompt, setPrompt] = useState('');
  const [fileIds, setFileIds] = useState<string[]>([]);

  const openAssistant = () => {
    setIsOpen(true);
  };

  const closeAssistant = () => {
    setIsOpen(false);
  };

  const openWithPrompt = (newPrompt: string) => {
    setPrompt(newPrompt);
    setFileIds([]);
    setSelectedConversationId(null); // Reset to welcome screen
    setIsOpen(true);
  };

  const openWithFileIds = (newFileIds: string[]) => {
    setPrompt('');
    setFileIds(newFileIds);
    setSelectedConversationId(null); // Reset to welcome screen
    setIsOpen(true);
  };

  const value = {
    isOpen,
    openAssistant,
    closeAssistant,
    selectedConversationId,
    setSelectedConversationId,
    prompt,
    openWithPrompt,
    fileIds,
    openWithFileIds,
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = () => {
  const context = useContext(AssistantContext);

  if (!context) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }

  return context;
};
